import { useState, useEffect, useCallback } from "react";
import {
  ClipboardList, AlertTriangle, CheckCircle2, Clock, ChevronRight, X,
  Ticket, Shield, FileText, BookOpen, Bell, Loader2, RefreshCw, Inbox
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";

const TYPE_CONFIG = {
  support_ticket: { icon: Ticket, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", label: "Support Ticket" },
  access_request: { icon: FileText, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", label: "Access Request" },
  ungraded_assessment: { icon: ClipboardList, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200", label: "Needs Grading" },
  overdue_compliance: { icon: Shield, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Overdue" },
  course_in_progress: { icon: BookOpen, color: "text-[#1e3a5f]", bg: "bg-slate-50", border: "border-slate-200", label: "In Progress" },
  notification: { icon: Bell, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Notification" },
};

const PRIORITY_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

const SEVERITY_COLORS = {
  warning: "bg-amber-500",
  alert: "bg-red-500",
  info: "bg-blue-500",
  success: "bg-emerald-500",
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const TaskItem = ({ item, onAction }) => {
  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.support_ticket;
  const Icon = cfg.icon;
  return (
    <div className={cn("flex items-center gap-4 p-4 border-b last:border-b-0 hover:bg-slate-50/50 transition-colors group")} data-testid={`task-item-${item.id}`}>
      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0", cfg.bg)}>
        <Icon className={cn("w-5 h-5", cfg.color)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#0f1b2d] truncate">{item.title}</span>
          {item.priority && (
            <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0", PRIORITY_COLORS[item.priority])}>
              {item.priority}
            </Badge>
          )}
        </div>
        {item.description && (
          <p className="text-xs text-slate-500 mt-0.5 truncate">{item.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1">
          <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0", cfg.bg, cfg.border, cfg.color)}>
            {cfg.label}
          </Badge>
          {item.user_name && <span className="text-[10px] text-slate-400">{item.user_name}</span>}
          <span className="text-[10px] text-slate-400">{timeAgo(item.created_at)}</span>
        </div>
      </div>
      {item.action_url && (
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-8 opacity-70 group-hover:opacity-100 transition-opacity"
          onClick={() => onAction(item.action_url)}
          data-testid={`task-action-${item.id}`}
        >
          {item.action_label || "View"} <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
};

const AttentionItem = ({ item, onAction, onDismiss }) => {
  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.notification;
  const Icon = cfg.icon;
  const sevColor = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.info;
  return (
    <div className="flex items-center gap-4 p-4 border-b last:border-b-0 group" data-testid={`attention-item-${item.id}`}>
      <div className="relative flex-shrink-0">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", cfg.bg)}>
          <Icon className={cn("w-5 h-5", cfg.color)} />
        </div>
        <span className={cn("absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white", sevColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-[10px] text-slate-400">{timeAgo(item.created_at)}</span>
        <p className="text-sm font-semibold text-[#0f1b2d]">{item.title}</p>
        {item.description && (
          <p className="text-xs text-slate-500 mt-0.5 truncate">{item.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {item.action_url && (
          <Button
            size="sm"
            className="text-xs h-8 bg-[#1e3a5f] hover:bg-[#162d4a]"
            onClick={() => onAction(item.action_url)}
          >
            {item.action_label || "Review"}
          </Button>
        )}
        <button
          onClick={() => onDismiss(item.id)}
          className="p-1.5 rounded-lg hover:bg-slate-100 opacity-40 group-hover:opacity-100 transition-opacity"
          data-testid={`dismiss-${item.id}`}
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    </div>
  );
};

const ActionCenterPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskFilter, setTaskFilter] = useState("open");
  const [dismissed, setDismissed] = useState(new Set());
  const role = user?.role || "learner";
  const firstName = user?.full_name?.split(" ")[0] || "there";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/action-center");
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAction = (url) => { if (url) navigate(url); };
  const handleDismiss = (id) => setDismissed(prev => new Set([...prev, id]));

  const layoutVariant = role === "super_admin" ? "super_admin" : role === "tenant_admin" ? "tenant_admin" : role === "manager" ? "manager" : "learner";
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  const tasks = data?.tasks || [];
  const attention = (data?.needs_attention || []).filter(a => !dismissed.has(a.id));

  if (!data && loading) {
    return (
      <AdminLayout variant={layoutVariant}>
        <div className="flex items-center justify-center h-[60vh]" data-testid="action-center-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout variant={layoutVariant}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="action-center-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-sm text-slate-500">{today}</p>
            <h1 className="text-3xl font-bold text-[#0f1b2d] mt-1">Hi, {firstName}</h1>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-action-center">
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} /> Refresh
          </Button>
        </div>

        {/* Tasks Section */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#1e3a5f]" />
              <h2 className="text-base font-bold text-[#0f1b2d]">Tasks</h2>
              {tasks.length > 0 && (
                <Badge className="bg-[#1e3a5f] text-white text-[10px] px-1.5 h-5">{tasks.length}</Badge>
              )}
            </div>
            <Tabs value={taskFilter} onValueChange={setTaskFilter} className="h-8">
              <TabsList className="h-8 bg-slate-100 p-0.5">
                <TabsTrigger value="open" className="text-xs h-7 px-3" data-testid="tasks-open-tab">Open</TabsTrigger>
                <TabsTrigger value="all" className="text-xs h-7 px-3" data-testid="tasks-all-tab">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <Card className="border shadow-none overflow-hidden">
            <CardContent className="p-0">
              {tasks.length === 0 ? (
                <div className="p-8 text-center">
                  <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500">You have no open tasks</p>
                  <p className="text-xs text-slate-400 mt-1">All caught up — nice work!</p>
                </div>
              ) : (
                tasks.map(task => (
                  <TaskItem key={task.id} item={task} onAction={handleAction} />
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Needs Attention Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h2 className="text-base font-bold text-[#0f1b2d]">Needs Attention</h2>
            {attention.length > 0 && (
              <Badge className="bg-amber-500 text-white text-[10px] px-1.5 h-5">{attention.length}</Badge>
            )}
          </div>
          <Card className="border shadow-none overflow-hidden">
            <CardContent className="p-0">
              {attention.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-3" />
                  <p className="text-sm text-slate-500">Nothing needs your attention</p>
                  <p className="text-xs text-slate-400 mt-1">Everything is running smoothly</p>
                </div>
              ) : (
                attention.map(item => (
                  <AttentionItem key={item.id} item={item} onAction={handleAction} onDismiss={handleDismiss} />
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ActionCenterPage;
